import { RootState, Subtask, Task } from 'constants/interfaces'
import keys from 'constants/keys'
import { useDashboardReview } from './hooks/useDashboardReview'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { calculateResultContextID } from 'redux/actions/tasks_v2'
import { userIdSelector } from 'redux/selectors/config'
import { extractSubstringAfterDelimiter } from 'utils'
import { navigateExecutionStack } from './model/navigation'
import { areSubtasksCompleted, findScopedResult, isResultCompleted, isResultReviewable } from './model/results'
export { parseRecurrenceTimestamp } from './utils'

export enum TaskOverviewContentType {
  MAIN = 'MAIN',
  VOTES_OVERVIEW = 'VOTES_OVERVIEW',
  MEDIA_SUBTASK_COMPLETING = 'MEDIA_SUBTASK_COMPLETING',
  OPEN_QUESTION_OVERVIEW = 'OPEN_QUESTION_OVERVIEW',
}

export interface ITaskOverviewContentStack {
  contentType: TaskOverviewContentType
  props?: any
}

interface TaskOverviewContextProps {
  contentStack: ITaskOverviewContentStack[]
  setContentStack: React.Dispatch<React.SetStateAction<ITaskOverviewContentStack[]>>
  openContent: (contentType: TaskOverviewContentType, props?: any) => void
  closeContent: () => void
  task: Task
  resultContextId: string
  isTaskCompleted: boolean
  isSubtaskCompleted: boolean
  isReadMode: boolean
  isShowResetMenu: boolean
  isComeFromDashboard: boolean
  mediaSubtasksOrdered: Subtask[]
  navigateToSubtask: (subtask: Subtask) => void
  currentSubtaskStatus: number | null
  /**
   * Whether this subtask has an actual submitted result an auditor could look at. SKIPPED
   * counts as terminal for overall task completion, but there's no media behind it, so it's
   * not reviewable. Callers decide whether this check applies (e.g. the media pager only
   * applies it in auditor/read mode — the executor still needs to reach not-yet-done subtasks).
   */
  isSubtaskReviewable: (subtask: Subtask) => boolean
  /** True while the executor is uploading media — used to lock the subtask pager. */
  isSubtaskBusy: boolean
  setIsSubtaskBusy: (uploading: boolean) => void
}

const TaskOverviewStackContext = createContext<TaskOverviewContextProps | undefined>(undefined)

export const TaskOverviewStackProvider: React.FC<{
  children: React.ReactNode
  task: Task
  onClose: VoidFunction
  isComeFromDashboard?: boolean
  content?: ITaskOverviewContentStack
}> = ({ children, task, content, onClose, isComeFromDashboard = false }) => {
  const review = useDashboardReview()
  const { tid, recipient_type, is_individual_results, retail_id_context_id, dashboardContextId, created_by } = task

  const { allResults, subtasks } = useSelector((state: RootState) => state.tasks_v2)

  const [isSubtaskBusy, setIsSubtaskBusy] = useState(false)

  const [contentStack, setContentStack] = useState<ITaskOverviewContentStack[]>(() => {
    if (content)
      return review && content.contentType === TaskOverviewContentType.MEDIA_SUBTASK_COMPLETING
        ? [
            {
              contentType: TaskOverviewContentType.MAIN,
              props: { selectedSubtaskId: content.props.selectedSubtask.st_id },
            },
            { ...content },
          ]
        : [{ ...content }]
    const firstSubtask = subtasks[tid]?.[0]
    const isMediaAiTask =
      task.type === keys.TASK_V2_TYPES.AI_TASK &&
      firstSubtask &&
      (firstSubtask.type === keys.SUBTASK_TYPES.IMAGE_SUBTASK ||
        firstSubtask.type === keys.SUBTASK_TYPES.VIDEO_SUBTASKS)
    if (isMediaAiTask) {
      return [
        { contentType: TaskOverviewContentType.MEDIA_SUBTASK_COMPLETING, props: { selectedSubtask: firstSubtask } },
      ]
    }
    return [{ contentType: TaskOverviewContentType.MAIN }]
  })
  const uid = useSelector(userIdSelector)!
  const {
    activeGroupID,
    config: { rid, tags, permission },
  } = useSelector((state: RootState) => state.config)

  const resultContextId = useMemo(() => {
    return calculateResultContextID({
      recipient_type: recipient_type,
      is_individual_results: is_individual_results,
      uid: uid,
      groupId: activeGroupID!,
      tagId: recipient_type === 2 ? extractSubstringAfterDelimiter(retail_id_context_id, rid) : null,
      retail_id_context_id: retail_id_context_id,
      rid,
      tags,
      dashboardContextId,
    })
  }, [activeGroupID, dashboardContextId, is_individual_results, recipient_type, retail_id_context_id, rid, tags, uid])

  const subtasksOfTask = subtasks[tid]

  const taskResults = allResults?.[tid] ?? []
  const isTaskCompleted = areSubtasksCompleted(subtasksOfTask ?? [], taskResults, resultContextId, task.recurrence_ts)

  const getSubtaskResult = useCallback(
    (stId: string) => findScopedResult(taskResults, stId, resultContextId, task.recurrence_ts),
    [taskResults, resultContextId, task.recurrence_ts]
  )

  // Single source of truth for the currently open subtask's result (top of stack).
  const currentSubtaskResult = useMemo(() => {
    const selectedSubtask = contentStack[contentStack.length - 1]?.props?.selectedSubtask
    if (!selectedSubtask) return undefined
    return getSubtaskResult(selectedSubtask.st_id)
  }, [contentStack, getSubtaskResult])

  const isSubtaskReviewable = useCallback(
    (subtask: Subtask) => isResultReviewable(getSubtaskResult(subtask.st_id)),
    [getSubtaskResult]
  )

  const isSubtaskCompleted = isResultCompleted(currentSubtaskResult)

  const isReadMode = !(resultContextId === uid || resultContextId === activeGroupID)

  const isShowResetMenu =
    isComeFromDashboard && (created_by === uid || permission?.tasks === keys.USER_ACTIONS_MAP.APP_ADMIN)

  // Ordered media subtasks (image/video) — used for the "Next subtask" pager.
  // Sort by order_index to match the MAIN list order (sortContentAndSubtasks does the same);
  // the raw subtasks array from the server is not in display order.
  const mediaSubtasksOrdered = useMemo(
    () =>
      (subtasksOfTask ?? [])
        .filter((st) => st.type === keys.SUBTASK_TYPES.IMAGE_SUBTASK || st.type === keys.SUBTASK_TYPES.VIDEO_SUBTASKS)
        .slice()
        .sort((a, b) => a.order_index - b.order_index),
    [subtasksOfTask]
  )

  // Status of the currently open media subtask — drives the pager status dot. null = no result.
  const currentSubtaskStatus = currentSubtaskResult ? currentSubtaskResult.status : null

  const openContent = useCallback((contentType: TaskOverviewContentType, props?: any) => {
    setContentStack((prevStack) => [...prevStack, { contentType, props }])
  }, [])

  // Keep MAIN mounted beneath media so its drafts and scroll containers survive navigation.
  const navigateToSubtask = useCallback((subtask: Subtask) => {
    setContentStack((previous) => navigateExecutionStack(previous, subtask))
  }, [])

  const closeContent = useCallback(() => {
    setContentStack((prevStack) => {
      const newStack = prevStack.slice(0, -1)
      return newStack
    })
  }, [])
  useEffect(() => {
    if (contentStack.length === 0) {
      onClose()
    }
  }, [contentStack, onClose])

  // Fallback: if AI task subtasks loaded after modal mount, redirect from MAIN to MEDIA_SUBTASK_COMPLETING
  useEffect(() => {
    if (content || task.type !== keys.TASK_V2_TYPES.AI_TASK) return
    const firstSubtask = subtasks[tid]?.[0]
    const isMediaSubtask =
      firstSubtask &&
      (firstSubtask.type === keys.SUBTASK_TYPES.IMAGE_SUBTASK ||
        firstSubtask.type === keys.SUBTASK_TYPES.VIDEO_SUBTASKS)
    if (!isMediaSubtask) return
    setContentStack((prev) => {
      if (prev.length === 1 && prev[0].contentType === TaskOverviewContentType.MAIN) {
        return [
          { contentType: TaskOverviewContentType.MEDIA_SUBTASK_COMPLETING, props: { selectedSubtask: firstSubtask } },
        ]
      }
      return prev
    })
  }, [subtasks[tid]]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <TaskOverviewStackContext.Provider
      value={{
        contentStack,
        setContentStack,
        openContent,
        closeContent,
        task,
        isTaskCompleted,
        isSubtaskCompleted,
        resultContextId,
        isReadMode,
        isShowResetMenu,
        isComeFromDashboard,
        mediaSubtasksOrdered,
        navigateToSubtask,
        currentSubtaskStatus,
        isSubtaskReviewable,
        isSubtaskBusy: isSubtaskBusy || !!review?.loading,
        setIsSubtaskBusy,
      }}
    >
      {children}
    </TaskOverviewStackContext.Provider>
  )
}

export const useOptionalTaskOverviewContext = () => useContext(TaskOverviewStackContext)

export const useTaskOverviewContext = () => {
  const context = useContext(TaskOverviewStackContext)
  if (!context) {
    throw new Error('useTaskOverviewContext must be used within a ContentStackProvider')
  }
  return context
}
